import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { Skull, Shield, Users } from "lucide-react";
import client, { formatErr } from "../lib/api";
import { toast } from "sonner";

export default function WeeklyCard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = () => client.get("/weekly-challenge").then((r) => setData(r.data));
    useEffect(() => {
        load();
    }, []);

    const log = async () => {
        setLoading(true);
        try {
            await client.post("/weekly-challenge/log");
            toast.success("You survived. Logged.");
            load();
        } catch (e) {
            toast.error(formatErr(e));
        } finally {
            setLoading(false);
        }
    };

    if (!data) return null;
    const c = data.challenge;
    const Icon = Icons[c.icon] || Icons.Zap;
    const alive = data.status === "alive";

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="weekly-card"
            className="relative overflow-hidden p-4 border-2"
            style={{
                background: alive
                    ? "linear-gradient(135deg, #062006 0%, #0d0d0d 60%)"
                    : "linear-gradient(135deg, #1a0606 0%, #0d0d0d 60%)",
                borderColor: alive ? "#39FF14" : "#FF3B30",
                boxShadow: alive
                    ? "0 0 24px rgba(57,255,20,0.2)"
                    : "0 0 24px rgba(255,59,48,0.25)",
            }}
        >
            <div className="absolute -right-4 -top-4 w-32 h-32 rounded-full blur-3xl"
                 style={{ background: alive ? "#39FF1430" : "#FF3B3030" }} />
            <div className="relative flex items-center gap-3">
                <div
                    className="w-12 h-12 flex items-center justify-center"
                    style={{
                        background: alive ? "#39FF1420" : "#FF3B3020",
                        border: `1px solid ${alive ? "#39FF14" : "#FF3B30"}`,
                    }}
                >
                    {alive ? <Shield size={20} className="text-neon-green" /> : <Skull size={20} className="text-neon-orange" />}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="label-cap text-[10px]" style={{ color: alive ? "#39FF14" : "#FF3B30" }}>
                        Weekly Elimination · {alive ? "Alive" : "At Risk"}
                    </div>
                    <div className="font-display font-black text-lg uppercase tracking-tight leading-tight">
                        {c.title}
                    </div>
                    <div className="text-xs text-zinc-400 mt-0.5">{c.tagline}</div>
                </div>
            </div>
            <div className="relative grid grid-cols-3 gap-2 mt-3">
                <div className="surface-3 p-2 text-center">
                    <div className="font-display font-black text-lg">{c.target}</div>
                    <div className="label-cap text-[9px] text-zinc-500">{c.unit}</div>
                </div>
                <div className="surface-3 p-2 text-center">
                    <div className="font-display font-black text-lg" style={{ color: data.days_left <= 1 ? "#FF3B30" : "#39FF14" }}>
                        {Math.max(0, data.days_left)}
                    </div>
                    <div className="label-cap text-[9px] text-zinc-500">Days Left</div>
                </div>
                <div className="surface-3 p-2 text-center">
                    <Users size={12} className="mx-auto text-zinc-500 mb-1" />
                    <div className="font-display font-black text-base">{data.survivors}</div>
                    <div className="label-cap text-[9px] text-zinc-500">Survivors</div>
                </div>
            </div>
            {!alive && (
                <button
                    onClick={log}
                    disabled={loading}
                    data-testid="weekly-log-btn"
                    className="btn-primary w-full py-2 mt-3 text-sm disabled:opacity-50"
                >
                    {loading ? "Logging..." : "I Survived — Lock It In"}
                </button>
            )}
            {alive && (
                <div className="mt-3 surface-3 py-2 text-center label-cap text-[11px] text-neon-green flex items-center justify-center gap-1">
                    <Shield size={11} /> Survived this week
                </div>
            )}
        </motion.div>
    );
}
